/**
 * useFlagAlerts - Real-time listener for flag alerts
 *
 * Subscribes to flag_alerts/ collection in Firestore
 * and returns active (not dismissed) alerts. Used by office/ops
 * dashboards to show real-time flag notifications from techs.
 *
 * Client-side filtering for dismissedAt — Firestore where('field', '==', null)
 * is unreliable for documents where the field is absent vs explicitly null.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { FlagAlert } from '@/types/schema';

interface UseFlagAlertsReturn {
  /** All active (not dismissed) flag alerts */
  alerts: FlagAlert[];
  /** Number of unacknowledged flag alerts */
  unacknowledgedCount: number;
  /** Whether the listener is still loading */
  loading: boolean;
}

/**
 * Subscribe to real-time flag alerts for a tenant.
 */
export function useFlagAlerts(tenantId: string | null): UseFlagAlertsReturn {
  const [alerts, setAlerts] = useState<FlagAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    // Track both primary and fallback unsubscribes for proper cleanup
    let primaryUnsub: (() => void) | null = null;
    let fallbackUnsub: (() => void) | null = null;

    // Fetch most-recent alerts for tenant, filter dismissed ones client-side.
    // Firestore where('dismissedAt', '==', null) doesn't reliably match
    // documents where the field is absent (only matches explicit null).
    // Capped at 100 most-recent — same cost-ceiling rationale as useSOSAlerts.
    const q = query(
      collection(db, 'flag_alerts'),
      where('tenantId', '==', tenantId),
      orderBy('flaggedAt', 'desc'),
      limit(100),
    );

    primaryUnsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as FlagAlert[];

        // Client-side filter for not dismissed
        const active = data.filter((a) => !a.dismissedAt);
        setAlerts(active);
        setLoading(false);
      },
      (err) => {
        // Fallback: simpler query without ordering if index is missing
        console.error('[useFlagAlerts] Listener error:', err.message);

        // Primary failed — clean it up
        if (primaryUnsub) { primaryUnsub(); primaryUnsub = null; }

        const fallbackQ = query(
          collection(db, 'flag_alerts'),
          where('tenantId', '==', tenantId),
          limit(100),
        );

        fallbackUnsub = onSnapshot(fallbackQ, (snapshot) => {
          const data = snapshot.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
            })) as FlagAlert[];

          const active = data
            .filter((a) => !a.dismissedAt)
            .sort((a, b) => (b.flaggedAt || '').localeCompare(a.flaggedAt || ''));

          setAlerts(active);
          setLoading(false);
        });
      },
    );

    return () => {
      if (primaryUnsub) primaryUnsub();
      if (fallbackUnsub) fallbackUnsub();
    };
  }, [tenantId]);

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;

  return { alerts, unacknowledgedCount, loading };
}
